/**
 * 本文の中で「どこの文言が変わったか」を求める。
 *
 * セル単位の地色は「この項目が変わった」までしか言わない。規則本文や期待結果のように 1 セルが
 * 数十文字ある欄では、地色が付いた時点で読み手は変更前と変更後を左右に並べ、目で 1 文字ずつ
 * 突き合わせることになる。**変わったのが 1 語でも、読む量はセル全体である。** 変わった区間まで
 * 出せば、読む量は変わった語だけで済む。
 *
 * 割り方は英数字の連なりと、それ以外の 1 文字である。日本語は語の切れ目が字面に出ないので、
 * 空白で割る方式（GitHub が英文でやっている割り方）は本文を 1 語として扱い、1 文字の直しで
 * セル全体が塗られる。**文字単位まで落とし、変わった文字の連なりだけをまとめる**ことで、
 * 「退会 → 解約申請」のような書き換えがその 4 文字だけの強調として出る。英数字を連なりの
 * ままにするのは、`AIRCLOSET` の 1 文字違いを 1 文字だけ塗ると識別子の同一性が読めなくなる
 * ためである。空白も連なりで 1 つに扱う（字下げの増減が 1 個ずつの強調に散らない）。
 *
 * 区間は **UTF-16 コード単位の半開区間**で返す。`String.prototype.slice` にそのまま渡せる形に
 * 揃えるためで、用語リンク（`term-segments.ts`）が同じ座標系で出現位置を持つので、両方を
 * 1 本の本文の上に重ねられる。
 *
 * 求めた区間はそのまま返さず、**読める形に整える 2 段の後処理を通す**。日本語では共通の助詞や
 * 単漢字が偶然一致して区間が虫食いに割れるので、間隔の短い区間をつなぐ（`BRIDGE_GAP`）。
 * それでも本文の大半を覆うなら、示すべき箇所が無いということなので強調そのものをやめる
 * （`GIVE_UP_RATIO`）。どちらも実データで測って決めた閾値である。
 *
 * 描画からは切り離してある。差分の正しさ（どこが変わった区間か）は描画を通さずに確かめられる
 * 性質で、混ぜると壊れたときにどちらが原因か切り分けられない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変更前と変更後の本文を突き合わせ、それぞれの側で変わった文字区間を求める。
 * @graph-connects none
 */

/** 本文の中の 1 区間。UTF-16 コード単位の半開区間で、`slice(start, end)` にそのまま渡せる。 */
export interface DiffRange {
  /** 開始位置（含む）。 */
  start: number;
  /** 終了位置（含まない）。 */
  end: number;
}

/** 変更前と変更後、それぞれの側で変わった区間。 */
export interface WordDiffRanges {
  /** 変更前の本文で「消えた」区間。 */
  before: readonly DiffRange[];
  /** 変更後の本文で「入った」区間。 */
  after: readonly DiffRange[];
}

/**
 * 本文の割り方。英数字の連なり・空白の連なり・それ以外の 1 文字（コードポイント）。
 *
 * `u` 付きの `.` はコードポイント 1 つに当たるので、サロゲートペア（絵文字・一部の漢字）が
 * 半分に割れない。半分に割れると、割れた片方だけが変わった区間として返り、描画側で文字化けする。
 * @graph-connects none
 */
const TOKEN_PATTERN = /[0-9A-Za-z_]+|\s+|./gsu;

/** 突き合わせの単位 1 つ。本文のどこから来たかを持つ。 @graph-connects none */
interface Token {
  /** 字面。突き合わせはこの値の一致で行う。 */
  text: string;
  /** 本文での開始位置。 */
  start: number;
  /** 本文での終了位置（含まない）。 */
  end: number;
}

/**
 * 突き合わせの表が大きくなりすぎない上限（升目の数）。
 *
 * 最長共通部分列は 2 本の長さの積に比例した表を作る。仕様の本文は長くても数百字だが、上限を
 * 置かないと、想定外に長い本文 1 件でブラウザが固まる。**超えたときは区間を求めず、セル全体を
 * 変わったものとして扱う**（`diffWords` が本文全体の 1 区間を返す）。強調が粗くなるだけで、
 * 変わったこと自体は地色が言う。
 * @graph-connects none
 */
const MAX_TABLE_CELLS = 1_000_000;

/**
 * 本文を突き合わせの単位に割る。
 *
 * @param text 割る本文
 * @returns 位置つきの単位の並び
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 本文を英数字の連なり・空白・1 文字の単位へ位置つきで割る。
 * @graph-connects none
 */
function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  for (const matched of text.matchAll(TOKEN_PATTERN)) {
    tokens.push({
      text: matched[0],
      start: matched.index,
      end: matched.index + matched[0].length,
    });
  }
  return tokens;
}

/**
 * 変更区間の間に挟まる未変更が、これ以下の文字数なら 1 本につなぐ。
 *
 * **日本語を文字単位で突き合わせると、共通の助詞や単漢字が偶然一致して変更区間が虫食いに
 * 分断される。** 英語は空白で語が切れるので起きないが、日本語では「の」「を」「た」「日」が
 * 至るところで一致する。本番の業務ロジック 1 件（197 文字の書き換え）で区間が 17 本に割れ、
 * 「2 日前より過去」「2 日前 → 1 日前」が別々に塗られて間の「→」「当日」が抜けた。
 * **どこが変わったかを示すはずの印が、読む手掛かりではなく模様になっていた。**
 *
 * 4 文字にしたのは実データで測って決めた。2 文字では虫食いが残り、6 文字では
 * 「退会した場合は、当月末」→「解約申請した場合は、翌月末」で変わっていない「した場合は、」まで
 * 塗ってしまう。
 * @graph-connects none
 */
const BRIDGE_GAP = 4;

/**
 * つないだあと、区間が 2 本以上あってこの割合を超えて塗るなら、語の強調そのものをやめる。
 *
 * **本文のほとんどが変わっているとき、細かい塗り分けは何も言っていない。** 「どこが変わったか」
 * という問い自体が成り立たないので、セルの地色だけで「この項目は書き換わった」と言うほうが
 * 正直である。1 本しかないときは割合が高くても残す — 1 本なら虫食いではなく「ここがまるごと
 * 変わった」であり、それは読みやすい（`AIRCLOSET_1` → `AIRCLOSET_2` は本文の 65% を占めるが
 * 1 本で、塗る値打ちがある）。
 * @graph-connects none
 */
const GIVE_UP_RATIO = 0.5;

/**
 * 近い区間をつなぐ。
 *
 * 変わった単位が続いたとき、単位ごとに区間を返すと描画側が 1 文字ずつ別の箱で塗る。字面は
 * 同じでも、塗りの境目が文字ごとに入るので「4 文字が変わった」ではなく「4 個の変更がある」と
 * 読める。接している区間はもちろん、**間に挟まる未変更が `BRIDGE_GAP` 以下なら 1 本にまとめる。**
 *
 * @param ranges 位置順に並んだ区間
 * @returns 近いものをつないだ区間
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 位置順の区間のうち、接しているものと間隔の短いものを 1 本につなぐ。
 * @graph-connects none
 */
function mergeNear(ranges: readonly DiffRange[]): DiffRange[] {
  const merged: DiffRange[] = [];
  for (const range of ranges) {
    const last = merged.length === 0 ? null : merged[merged.length - 1];
    if (last !== null && range.start - last.end <= BRIDGE_GAP) {
      last.end = range.end;
      continue;
    }
    merged.push({ start: range.start, end: range.end });
  }
  return merged;
}

/** 区間が覆う文字数の合計。 @graph-connects none */
function coveredLength(ranges: readonly DiffRange[]): number {
  return ranges.reduce((total, range) => total + (range.end - range.start), 0);
}

/**
 * 塗りすぎで印の意味が消えているか。
 *
 * 区間が 1 本しかないなら虫食いではないので、どれだけ広くても残す。2 本以上に割れていて、
 * なお本文の大半を覆うなら、それは「どこが変わったか」を示せていない。
 *
 * @param ranges つないだあとの区間
 * @param length 本文の長さ
 * @returns 語の強調をやめるべきなら `true`
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business つないだ区間が本文の大半を覆って印の意味を失っていないかを判定する。
 * @graph-connects none
 */
function isTooBroad(ranges: readonly DiffRange[], length: number): boolean {
  return ranges.length >= 2 && coveredLength(ranges) / length > GIVE_UP_RATIO;
}

/** 本文全体を 1 区間として返す。空文字なら区間そのものを持たない。 @graph-connects none */
function wholeText(text: string): DiffRange[] {
  return text === '' ? [] : [{ start: 0, end: text.length }];
}

/**
 * 最長共通部分列の表を作る。`table[i * width + j]` は「変更前の i 番目以降と変更後の j 番目
 * 以降で一致させられる最大個数」である。
 *
 * @param before 変更前の単位
 * @param after 変更後の単位
 * @returns 表と 1 行の幅
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変更前と変更後の単位列から、最長共通部分列の長さの表を組む。
 * @graph-connects none
 */
function buildLcsTable(
  before: readonly Token[],
  after: readonly Token[],
): { table: Uint32Array; width: number } {
  const width = after.length + 1;
  const table = new Uint32Array((before.length + 1) * width);

  for (let i = before.length - 1; i >= 0; i -= 1) {
    for (let j = after.length - 1; j >= 0; j -= 1) {
      table[i * width + j] =
        before[i].text === after[j].text
          ? table[(i + 1) * width + (j + 1)] + 1
          : Math.max(table[(i + 1) * width + j], table[i * width + (j + 1)]);
    }
  }

  return { table, width };
}

/**
 * 変更前と変更後の本文を突き合わせ、それぞれの側で変わった区間を求める。
 *
 * **一致しない単位だけを区間にする。** 位置が動いただけの単位（前に 1 語挿されて後ろが全部
 * ずれた場合）は最長共通部分列が一致として拾うので、区間には出ない。位置で突き合わせると、
 * 1 語の挿入で以降の全部が「変わった」と出る。
 *
 * 本文が同じなら両側とも 0 件を返す。片側が空文字なら、もう片側の全体が 1 区間になる。
 *
 * @param before 変更前の本文
 * @param after 変更後の本文
 * @returns 変更前で消えた区間と、変更後で入った区間
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変更前と変更後の本文を突き合わせ、消えた区間と入った区間を求める。
 * @graph-connects none
 */
export function diffWords(before: string, after: string): WordDiffRanges {
  if (before === after) return { before: [], after: [] };
  if (before === '' || after === '') {
    return { before: wholeText(before), after: wholeText(after) };
  }

  const beforeTokens = tokenize(before);
  const afterTokens = tokenize(after);

  if ((beforeTokens.length + 1) * (afterTokens.length + 1) > MAX_TABLE_CELLS) {
    return { before: wholeText(before), after: wholeText(after) };
  }

  const { table, width } = buildLcsTable(beforeTokens, afterTokens);
  const beforeRanges: DiffRange[] = [];
  const afterRanges: DiffRange[] = [];
  let i = 0;
  let j = 0;

  while (i < beforeTokens.length && j < afterTokens.length) {
    const beforeToken = beforeTokens[i];
    const afterToken = afterTokens[j];

    if (beforeToken.text === afterToken.text) {
      i += 1;
      j += 1;
      continue;
    }
    /*
     * 一致しないときは、残りを長く一致させられるほうへ進む。変更前を進めればその単位が
     * 「消えた」、変更後を進めればその単位が「入った」ことになる。
     */
    if (table[(i + 1) * width + j] >= table[i * width + (j + 1)]) {
      beforeRanges.push({ start: beforeToken.start, end: beforeToken.end });
      i += 1;
    } else {
      afterRanges.push({ start: afterToken.start, end: afterToken.end });
      j += 1;
    }
  }

  /*
   * 片方が先に尽きたら、残りはすべて「消えた」または「入った」である。突き合わせる相手が
   * もう無いので、一致を探す余地が無い。
   */
  for (const token of beforeTokens.slice(i)) {
    beforeRanges.push({ start: token.start, end: token.end });
  }
  for (const token of afterTokens.slice(j)) {
    afterRanges.push({ start: token.start, end: token.end });
  }

  const mergedBefore = mergeNear(beforeRanges);
  const mergedAfter = mergeNear(afterRanges);
  /*
   * **判定は両側まとめて行う。** 片側だけ強調をやめると、左右比較で左は塗られて右は塗られない
   * 状態になり、読み手には「右は変わっていない」と読める。
   */
  if (isTooBroad(mergedBefore, before.length) || isTooBroad(mergedAfter, after.length)) {
    return { before: [], after: [] };
  }

  return { before: mergedBefore, after: mergedAfter };
}

/**
 * 本文を「変わった区間」と「変わっていない区間」の並びに割る。
 *
 * 区間は本文全体の座標で来るが、割る相手は本文の一部でありうる（用語リンクや変数参照で
 * 切れた後の 1 片）。**`offset` で相手の先頭が全体のどこかを渡す**ことで、切れた後も同じ区間を
 * 当てられる。切る前に区間を相対座標へ直すと、切り方を変えるたびに座標の直し方も書き換える
 * ことになり、片方だけ直った状態が生まれる。
 *
 * @param text 割る本文（全体の一部でよい）
 * @param offset `text` の先頭が全体本文の何文字目か
 * @param ranges 全体本文の座標での変更区間
 * @returns 変わったかどうかつきの片の並び。`text` が空文字なら 0 件
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 本文を変更区間で割り、変わったかどうかつきの片の並びにする。
 * @graph-connects none
 */
export function splitByRanges(
  text: string,
  offset: number,
  ranges: readonly DiffRange[],
): { text: string; changed: boolean }[] {
  if (text === '') return [];

  const end = offset + text.length;
  const parts: { text: string; changed: boolean }[] = [];
  let cursor = offset;

  for (const range of ranges) {
    /* この片に掛からない区間は読み飛ばす。区間は位置順に並んでいるので打ち切りはしない。 */
    const from = Math.max(range.start, cursor);
    const to = Math.min(range.end, end);
    if (from >= to) continue;

    if (from > cursor) {
      parts.push({ text: text.slice(cursor - offset, from - offset), changed: false });
    }
    parts.push({ text: text.slice(from - offset, to - offset), changed: true });
    cursor = to;
  }

  if (cursor < end) parts.push({ text: text.slice(cursor - offset), changed: false });

  return parts;
}
